import { OFFER_LIST_FILTERS, type OfferListFilter } from '@fos/shared/contracts/offers';
import { OFFER_LIST_FILTER_LABELS } from '@fos/shared/labels';
import { FileText, Plus, Search } from 'lucide-react';
import { type ReactNode, useDeferredValue, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { trpc } from '../../../lib/trpc';
import { useAccess } from '../../../lib/useAccess';
import { Button } from '../../../ui/Button';
import { Input } from '../../../ui/Input';
import { Page } from '../../../ui/Page';
import { PageHeader } from '../../../ui/PageHeader';
import { Section } from '../../../ui/Section';
import { Segmented } from '../../../ui/Segmented';
import { EmptyState, ErrorState, ListSkeleton } from '../../../ui/States';
import { MobileFab, SEGMENTED_TOUCH, TOUCH_BUTTON, useIsPhone } from '../../mobile';
import { ModulePage } from '../../modules/pages/ModulePage';
import { NewOfferDialog } from '../components/NewOfferDialog';
import { OfferRow } from '../components/OfferRow';
import { FILTER_SLUGS, filterFromSlug } from '../offer-format';

const EMPTY: Record<OfferListFilter, { title: string; description: string }> = {
  open: {
    title: 'Brak otwartych ofert',
    description: 'Robocze, czekające na właściciela i wysłane oferty pojawią się tutaj.',
  },
  accepted: { title: 'Brak przyjętych ofert', description: 'Oferta przyjęta przez klienta trafi tutaj.' },
  rejected: { title: 'Brak odrzuconych ofert', description: 'Odrzucone oferty zostają w historii klienta.' },
  all: { title: 'Brak ofert', description: 'Pierwszą ofertę przygotujesz w kilka minut: zakres, godziny i warunki.' },
};

/**
 * Lista ofert (/oferty, także z ⌘K; ekran czeka na projekt w Figmie — wzorzec list Klienci / Projekty):
 * zakładki Otwarte / Przyjęte / Odrzucone / Wszystkie (w adresie), wyszukiwanie po tytule i kliencie, „Nowa oferta”
 * (moduł Klienci = Edycja; `?nowa=1` otwiera formularz). Wartość tylko z „Widzi stawki i kwoty”.
 */
export function OffersListPage() {
  const access = useAccess();
  if (!access.can('clients')) return <ModulePage module="clients" />;
  return <OffersList />;
}

function OffersList() {
  const access = useAccess();
  const canAdd = access.can('clients', 'edit');
  const [params, setParams] = useSearchParams();
  const filter = filterFromSlug(params.get('filtr'));
  const [q, setQ] = useState('');
  const query = useDeferredValue(q.trim());
  const [dialogOpen, setDialogOpen] = useState(false);
  const phone = useIsPhone();

  // ?nowa=1 (np. z ⌘K): otwórz formularz i wyczyść parametr
  // biome-ignore lint/correctness/useExhaustiveDependencies: tylko przy wejściu z parametrem
  useEffect(() => {
    if (params.get('nowa') !== '1') return;
    if (canAdd) setDialogOpen(true);
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.delete('nowa');
        return p;
      },
      { replace: true },
    );
  }, [params.get('nowa')]);

  const list = trpc.offers.list.useQuery({ filter, ...(query.length >= 2 ? { q: query } : {}) });
  const setFilter = (next: OfferListFilter) =>
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (next === 'open') p.delete('filtr');
        else p.set('filtr', FILTER_SLUGS[next]);
        return p;
      },
      { replace: true },
    );

  let body: ReactNode;
  if (list.isPending) body = <ListSkeleton rows={4} />;
  else if (list.isError) body = <ErrorState onRetry={() => void list.refetch()} retrying={list.isFetching} />;
  else if (list.data.length === 0)
    body =
      query.length >= 2 ? (
        <EmptyState icon={Search} title="Nic nie pasuje" description="Spróbuj innej nazwy oferty albo klienta." />
      ) : (
        <EmptyState
          icon={FileText}
          title={EMPTY[filter].title}
          description={EMPTY[filter].description}
          action={
            canAdd && filter !== 'accepted' && filter !== 'rejected' ? (
              <Button variant="secondary" icon={Plus} onClick={() => setDialogOpen(true)} className={TOUCH_BUTTON}>
                Nowa oferta
              </Button>
            ) : undefined
          }
        />
      );
  else
    body = (
      <ul aria-label="Oferty">
        {list.data.map((o) => (
          <li key={o.id}>
            <OfferRow offer={o} />
          </li>
        ))}
      </ul>
    );

  return (
    <Page>
      <PageHeader
        title="Oferty"
        meta="Oferty dla klientów z historią wersji. Wysyłka zawsze przez Akceptacje."
        actions={
          canAdd ? (
            <Button
              variant="primary"
              icon={Plus}
              onClick={() => setDialogOpen(true)}
              className={`max-md:hidden ${TOUCH_BUTTON}`}
            >
              Nowa oferta
            </Button>
          ) : undefined
        }
      />
      <div className="mt-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="-mx-4 overflow-x-auto px-4 [scrollbar-width:none] md:mx-0 md:px-0">
          <Segmented
            aria-label="Zakładki ofert"
            className={SEGMENTED_TOUCH}
            value={filter}
            onValueChange={setFilter}
            options={OFFER_LIST_FILTERS.map((f) => ({ value: f, label: OFFER_LIST_FILTER_LABELS[f] }))}
          />
        </div>
        <div className="md:w-72">
          <Input
            icon={Search}
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Szukaj oferty albo klienta"
            aria-label="Szukaj ofert"
          />
        </div>
      </div>
      <Section className="mt-4">{body}</Section>
      {canAdd && phone && (
        <MobileFab icon={Plus} onClick={() => setDialogOpen(true)}>
          Nowa oferta
        </MobileFab>
      )}
      {canAdd && <NewOfferDialog open={dialogOpen} onOpenChange={setDialogOpen} />}
    </Page>
  );
}
